import availableIcons from './data/icons.json';
import legacyMapping from './data/legacy.json';
import * as iconImports from './svg-imports';

export class IconLoader {
  private static readonly ICON_PREFIX = 'icon-';

  /**
   * Get all available icons.
   *
   * @param prefixKeys Returns all options with keys prefixed with "icon-"
   */
  public options(prefixKeys: boolean = false): Record<string, string> {
    if (prefixKeys) {
      const mapped: Record<string, string> = {};

      for (const [key, value] of Object.entries(availableIcons)) {
        mapped[this.prefixKey(key)] = value;
      }

      return mapped;
    }

    return { ...availableIcons };
  }

  /**
   * Resolve the icon key, if it exists in the available icons.
   *
   * @param key The icon key to resolve
   * @param prefixed Resolve the icon name with "icon-" prefix
   */
  public resolve(key: string | null | undefined, prefixed: boolean = false): string | null {
    if (key == null) {
      return null;
    }

    const normalizedKey = this.normalize(key);

    if (!(normalizedKey in availableIcons)) {
      return null;
    }

    return prefixed ? this.prefixKey(normalizedKey) : normalizedKey;
  }

  /**
   * Get the SVG contents for the given icon key.
   *
   * @param key The icon key to get the SVG for
   */
  public getSvg(key: string | null | undefined): string | null {
    const resolvedKey = this.resolve(key);

    if (resolvedKey === null) {
      return null;
    }

    const importKey = this.toImportKey(resolvedKey);

    return (iconImports as unknown as Record<string, string>)[importKey] ?? null;
  }

  /**
   * Prefix a normalized key with the "icon-" prefix.
   */
  private prefixKey(normalizedKey: string): string {
    return IconLoader.ICON_PREFIX + normalizedKey;
  }

  /**
   * Convert a kebab-case icon key to its PascalCase import name.
   * E.g. "account-settings" becomes "IconAccountSettings"
   */
  private toImportKey(key: string): string {
    return 'Icon' + key.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('');
  }

  /**
   * Normalize the incoming key to an unprefixed version, apply necessary legacy mapping.
   *
   * @param key The key of the icon to normalize
   */
  private normalize(key: string): string {
    // Remove prefix
    if (key.startsWith(IconLoader.ICON_PREFIX)) {
      key = key.substring(IconLoader.ICON_PREFIX.length);
    }

    return legacyMapping[key as keyof typeof legacyMapping] ?? key;
  }
}

export const SvgIconType = {
	account: 'account',
	accountSettings: 'account-settings',
	active: 'active',
	addShoppingCart: 'add-shopping-cart',
	alert: 'alert',
	align: 'align',
	alignJustify: 'align-justify',
	alignLeft: 'align-left',
	alignRight: 'align-right',
	anchor: 'anchor',
	archive: 'archive',
	arrowCircleDown: 'arrow-circle-down',
	arrowCircleLeft: 'arrow-circle-left',
	arrowCircleRight: 'arrow-circle-right',
	arrowCircleUp: 'arrow-circle-up',
	arrowDown: 'arrow-down',
	arrowLeft: 'arrow-left',
	arrowLeftDown: 'arrow-left-down',
	arrowLeftUp: 'arrow-left-up',
	arrowRight: 'arrow-right',
	arrowRightDown: 'arrow-right-down',
	arrowRightUp: 'arrow-right-up',
	arrowUp: 'arrow-up',
	at: 'at',
	attachment: 'attachment',
	award: 'award',
	bagage: 'bagage',
	bell: 'bell',
	bike: 'bike',
	bluetooth: 'bluetooth',
	book: 'book',
	box: 'box',
	bulletCircle: 'bullet-circle',
	bulletPoint: 'bullet-point',
	calendar: 'calendar',
	calendarAlt: 'calendar-alt',
	calendarComplete: 'calendar-complete',
	calendarRounded: 'calendar-rounded',
	camera: 'camera',
	card: 'card',
	cardAlt: 'card-alt',
	charge: 'charge',
	chart1: 'chart-1',
	chart2: 'chart-2',
	chat: 'chat',
	check: 'check',
	checkCircle: 'check-circle',
	checkSquare: 'check-square',
	checkmarkCircle: 'checkmark-circle',
	cheers: 'cheers',
	chevronDown: 'chevron-down',
	chevronLeft: 'chevron-left',
	chevronRight: 'chevron-right',
	chevronUp: 'chevron-up',
	chevronsDown: 'chevrons-down',
	chevronsLeft: 'chevrons-left',
	chevronsRight: 'chevrons-right',
	chevronsUp: 'chevrons-up',
	circle: 'circle',
	clipboard: 'clipboard',
	clipboardFilled: 'clipboard-filled',
	clock: 'clock',
	clockFilled: 'clock-filled',
	clockTimeBack: 'clock-time-back',
	close: 'close',
	cloud: 'cloud',
	cloudDrizzle: 'cloud-drizzle',
	cloudLightRain: 'cloud-light-rain',
	cloudLightning: 'cloud-lightning',
	cloudRain: 'cloud-rain',
	code: 'code',
	coffee: 'coffee',
	compas: 'compas',
	cornerDownLeft: 'corner-down-left',
	cornerDownRight: 'corner-down-right',
	cornerLeftDown: 'corner-left-down',
	cornerLeftUp: 'corner-left-up',
	cornerRightDown: 'corner-right-down',
	cornerRightUp: 'corner-right-up',
	cornerUpLeft: 'corner-up-left',
	cornerUpRight: 'corner-up-right',
	counting: 'counting',
	cross: 'cross',
	decrease: 'decrease',
	diamond: 'diamond',
	diner: 'diner',
	discount: 'discount',
	dot: 'dot',
	dryCleaning: 'dry-cleaning',
	edit: 'edit',
	edit3: 'edit-3',
	electric: 'electric',
	email: 'email',
	euro: 'euro',
	externalLink: 'external-link',
	extreme: 'extreme',
	eye: 'eye',
	eyeOff: 'eye-off',
	facebook: 'facebook',
	fastForward: 'fast-forward',
	film: 'film',
	filter: 'filter',
	flag: 'flag',
	folder: 'folder',
	gift: 'gift',
	globe: 'globe',
	google: 'google',
	grade: 'grade',
	grid: 'grid',
	hash: 'hash',
	headphones: 'headphones',
	healing: 'healing',
	heart: 'heart',
	heartPlus: 'heart-plus',
	helpCircle: 'help-circle',
	home: 'home',
	homeAlt: 'home-alt',
	image: 'image',
	increase: 'increase',
	info: 'info',
	infoFilled: 'info-filled',
	instagram: 'instagram',
	label: 'label',
	layers: 'layers',
	lightBulb: 'light-bulb',
	link: 'link',
	linkedin: 'linkedin',
	listFilled: 'list-filled',
	loading: 'loading',
	location: 'location',
  locationFilled: 'location-filled',
	lock: 'lock',
	logIn: 'log-in',
	logOut: 'log-out',
	logoMark: 'logo-mark',
	logout: 'logout',
	mail: 'mail',
	maleFemale: 'male-female',
	man: 'man',
	map: 'map',
	maximize: 'maximize',
	maximize2: 'maximize-2',
	menu: 'menu',
	message: 'message',
	min: 'min',
	minus: 'minus',
	mix: 'mix',
	moon: 'moon',
	moreVertical: 'more-vertical',
	move: 'move',
	navigation: 'navigation',
	noCamera: 'no-camera',
	noVideo: 'no-video',
	opties1: 'opties1',
	options: 'options',
	package: 'package',
	pencil: 'pencil',
	percentage: 'percentage',
	persons: 'persons',
	phone: 'phone',
	play: 'play',
	plus: 'plus',
	plusCircle: 'plus-circle',
	plusCircleFilled: 'plus-circle-filled',
	plusSquare: 'plus-square',
	pocket: 'pocket',
	power: 'power',
	preferences: 'preferences',
	printer: 'printer',
	profile: 'profile',
	qrCodeScanner: 'qr-code-scanner',
	questionMarkCircle: 'question-mark-circle',
	quote: 'quote',
	radio: 'radio',
	redeem: 'redeem',
	refreshCcw: 'refresh-ccw',
	refreshCw: 'refresh-cw',
	relaxation: 'relaxation',
	repeat: 'repeat',
	restaurant: 'restaurant',
	rewind: 'rewind',
	rotateCcw: 'rotate-ccw',
	rotateCw: 'rotate-cw',
	rss: 'rss',
	scanBarcode: 'scan-barcode',
	scent: 'scent',
	scissors: 'scissors',
	search: 'search',
	send: 'send',
	server: 'server',
	settings: 'settings',
	share: 'share',
	share2: 'share-2',
	shield: 'shield',
	shoppingBagHigh: 'shopping-bag-high',
	shoppingBagLow: 'shopping-bag-low',
	shoppingCart: 'shopping-cart',
	sliders: 'sliders',
	sound: 'sound',
	star: 'star',
	starHalf: 'star-half',
	starOutline: 'star-outline',
	stressManagement: 'stress-management',
	suitcaseFilled: 'suitcase-filled',
	sun: 'sun',
	sunrise: 'sunrise',
	sunset: 'sunset',
	swipe: 'swipe',
	tag: 'tag',
	target: 'target',
	thermometer: 'thermometer',
	thermostat: 'thermostat',
	thumbsDown: 'thumbs-down',
	thumbsUp: 'thumbs-up',
	tick: 'tick',
	ticket: 'ticket',
	tiktok: 'tiktok',
	toggleLeft: 'toggle-left',
	toggleRight: 'toggle-right',
	tooltip: 'tooltip',
	towel: 'towel',
	trash: 'trash',
	trashAlt: 'trash-alt',
	treatment: 'treatment',
	tv: 'tv',
	type: 'type',
	umbrella: 'umbrella',
	underline: 'underline',
	upload: 'upload',
	uploadCloud: 'upload-cloud',
	user: 'user',
	userCheck: 'user-check',
	userMinus: 'user-minus',
	userPlus: 'user-plus',
	users: 'users',
	video: 'video',
	videos: 'videos',
	volume: 'volume',
	volume1: 'volume-1',
	volume2: 'volume-2',
	volumeX: 'volume-x',
	voucher: 'voucher',
	waterDrop: 'water-drop',
	wifi: 'wifi',
	wifiOff: 'wifi-off',
	wineGlass: 'wine-glass',
	woman: 'woman',
	x: 'x',
	youtube: 'youtube',
	zoomIn: 'zoom-in',
	zoomOut: 'zoom-out',
	zoover: 'zoover',
} as const;

export type SvgIconType = typeof SvgIconType[keyof typeof SvgIconType];
